import { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChefHat, ShieldCheck, UtensilsCrossed, ArrowRight, Eye } from "lucide-react";
import { BrandMark } from "@/components/Brand";

const PERSONAS = [
  {
    id: "customer",
    title: "Customer",
    blurb: "Browse the customer flow — service selector, weekly menu, live chef tracking, AI forecast.",
    icon: UtensilsCrossed,
    color: "from-orange-500 to-amber-500",
    after: "/app",
  },
  {
    id: "chef",
    title: "Chef",
    blurb: "Step into the chef's day — assignments, time-gated slots, GPS streaming, arrival/exit selfies.",
    icon: ChefHat,
    color: "from-emerald-600 to-teal-500",
    after: "/chef",
  },
  {
    id: "admin",
    title: "Admin",
    blurb: "Run the operation — leads, mapping, calendar, live map, password resets, chef availability clock.",
    icon: ShieldCheck,
    color: "from-violet-600 to-fuchsia-500",
    after: "/admin",
  },
];

export default function PersonaPreview() {
  const [busy, setBusy] = useState(null);
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const autoLaunched = useRef(false);

  // Allow direct deep-link: /preview?as=chef — auto-launches
  const preset = params.get("as");

  const launch = async (personaId) => {
    setBusy(personaId);
    try {
      const { data } = await api.post("/auth/preview", { persona: personaId });
      localStorage.setItem("chulha_token", data.token);
      await refresh();
      const dest = PERSONAS.find(p => p.id === personaId).after;
      toast.success(`Previewing as ${data.user.name}`);
      nav(dest);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Preview launch failed");
    } finally {
      setBusy(null);
    }
  };

  // Auto-launch once if ?as=customer|chef|admin (guarded against retry loops).
  useEffect(() => {
    if (autoLaunched.current) return;
    if (!preset) return;
    if (!PERSONAS.find(p => p.id === preset)) return;
    autoLaunched.current = true;
    launch(preset);
    // `launch` is stable for this purpose; we want this to fire exactly once per ?as=
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset]);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="preview-brand">
            <div className="w-8 h-8 flex items-center justify-center"><BrandMark size={32}/></div>
          </Link>
          <Link to="/login" className="text-sm text-muted-foreground hover:text-primary" data-testid="preview-login">Sign in →</Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-12 sm:py-16">
        <div className="max-w-2xl mb-10">
          <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-bold text-primary mb-3">
            <Eye size={14}/> Preview mode
          </div>
          <h1 className="heading text-4xl sm:text-5xl font-bold leading-tight">Step into any persona.</h1>
          <p className="text-base text-muted-foreground mt-3">
            Pick a role below. We&rsquo;ll sign you in as a demo account so you can explore the full experience.
            No setup, no commitment &mdash; switch any time.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PERSONAS.map(p => {
            const Icon = p.icon;
            const isBusy = busy === p.id;
            return (
              <div
                key={p.id}
                className="group relative rounded-2xl border border-border bg-card overflow-hidden hover:shadow-lg hover:border-primary/30 transition-all"
                data-testid={`persona-card-${p.id}`}
              >
                <div className={`h-32 bg-gradient-to-br ${p.color} relative overflow-hidden`}>
                  <div className="absolute -bottom-4 -right-4 opacity-20 transition-transform group-hover:scale-110">
                    <Icon size={120} strokeWidth={1.2} className="text-white"/>
                  </div>
                  <div className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                    <Icon size={20} className="text-white"/>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="heading text-2xl font-bold mb-2">{p.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed min-h-[60px]">{p.blurb}</p>
                  <Button
                    onClick={() => launch(p.id)} disabled={!!busy}
                    className={`mt-4 w-full rounded-full bg-gradient-to-r ${p.color} text-white hover:opacity-90 border-0 group/btn`}
                    data-testid={`preview-launch-${p.id}`}
                  >
                    {isBusy ? "Launching..." : <>Preview as {p.title} <ArrowRight size={14} className="ml-1.5 transition-transform group-hover/btn:translate-x-1"/></>}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-10 p-4 rounded-xl bg-muted/50 border border-border text-xs text-muted-foreground space-y-1.5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <b>Shareable persona links:</b> bookmark or send to teammates
            </div>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/uat/download`}
              className="text-primary font-medium hover:underline inline-flex items-center gap-1"
              data-testid="uat-download"
            >
              📥 Download UAT test pack (Excel)
            </a>
          </div>
          <div className="font-mono text-[11px] grid sm:grid-cols-3 gap-2 pt-1">
            <code className="bg-card border border-border rounded px-2 py-1.5">/preview?as=customer</code>
            <code className="bg-card border border-border rounded px-2 py-1.5">/preview?as=chef</code>
            <code className="bg-card border border-border rounded px-2 py-1.5">/preview?as=admin</code>
          </div>
          <p className="pt-2 text-[11px]">Note: preview mode signs you in as a demo account. Sign out to exit.</p>
        </div>
      </div>
    </div>
  );
}
